import { environment } from '@env/environment';

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]', '::1'];

/**
 * The API sets its auth cookies (AuthToken, SessionId) on the canonical host's site.
 * Reaching the app on any other hostname - typically the App Service default
 * *.azurewebsites.net name, which stays resolvable after the custom domain is bound -
 * makes those cookies cross-site. Safari blocks cross-site cookies outright, so the
 * user would be able to submit the login form and then get 401 on every call after it.
 *
 * Sends the user to the same path on the canonical origin instead.
 * Returns true when a redirect was started, in which case the app must not bootstrap.
 */
export function redirectToCanonicalHost(): boolean {
  // Never redirect away from a local dev server - the port it happens to be
  // running on is not worth guessing, and cookies are same-site there anyway.
  if (LOCAL_HOSTNAMES.includes(window.location.hostname)) {
    return false;
  }

  let canonicalOrigin: string;
  try {
    canonicalOrigin = new URL(environment.frontEndUrl).origin;
  } catch {
    // A malformed frontEndUrl must not take the app down.
    console.error('Invalid environment.frontEndUrl, skipping canonical host check:', environment.frontEndUrl);
    return false;
  }

  if (window.location.origin === canonicalOrigin) {
    return false;
  }

  const { pathname, search, hash } = window.location;
  // replace() so the wrong host does not end up in history and the back button
  // cannot walk the user straight back onto it.
  window.location.replace(`${canonicalOrigin}${pathname}${search}${hash}`);
  return true;
}
